import * as Rx from 'rxjs';
import type { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import type { ICard } from 'v2/components/Shared/SelectCreditCard';
import type { ICustomer, ICardApiResponse } from 'v2/interfaces/customer';
import type { ISubscriptions } from 'v2/interfaces/subscriptions';

import apiService from './api';
import type { ApiService } from './api';

/**
 * Service responsible for controlling `Customer` component.
 *
 * @class CustomerService
 */
class CustomerService {
  private loadingCards$ = new Rx.BehaviorSubject<boolean>(false);
  private loadingCardsSubscriptions$ = new Rx.BehaviorSubject<boolean>(false);
  private loadingCardUsage$ = new Rx.BehaviorSubject<boolean>(true);
  private cardsList$ = new Rx.BehaviorSubject<ICardApiResponse[]>([]);
  private cardsListSubscriptions$ = new Rx.BehaviorSubject<ICardApiResponse[]>([]);
  private cardUsage$ = new Rx.BehaviorSubject<ISubscriptions[]>([]);

  constructor(private apiService: ApiService) {}

  /**
   * @method setReferred
   * @return {Observable}
   */
  public setReferred(customerId: number, referredId: string) {
    return this.apiService.put(`customers/${customerId}`, { indication_hash: referredId });
  }

  public joinBlackFriday() {
    return this.apiService.post('customers/blackfriday', {});
  }

  public getById(customerId: number): Observable<ICustomer> {
    if (!customerId) {
      return Rx.of(null);
    }

    return this.apiService.get(`customers/${customerId}`).pipe(map(response => response.data));
  }

  public loadingCards() {
    return this.loadingCards$;
  }

  public loadingCardsSubscriptions() {
    return this.loadingCardsSubscriptions$;
  }

  public getCardsList() {
    return this.cardsList$;
  }

  public loadCardsList() {
    this.loadingCards$.next(true);
    return this.apiService.get('/payment-methods', { current_page: 1, per_page: 1000 }).pipe(
      map(response => response.data.filter((item: ICardApiResponse) => item.holder)),
      tap(() => this.loadingCards$.next(false)),
      tap(data => this.cardsList$.next(data))
    );
  }

  public getCardsListSubscriptions() {
    return this.cardsListSubscriptions$;
  }

  public getCardsListSubscriptionsRequest(): Observable<ICardApiResponse[]> {
    return this.apiService
      .get('/payment-methods/subscriptions', { current_page: 1, per_page: 1000 })
      .pipe(map(response => response.data.filter((item: ICardApiResponse) => item.holder)));
  }

  public loadCardsListSubscriptions() {
    this.loadingCardsSubscriptions$.next(true);
    return this.apiService.get('/payment-methods/subscriptions', { current_page: 1, per_page: 1000 }).pipe(
      map(response => response.data.filter((item: ICardApiResponse) => item.holder)),
      tap(() => this.loadingCardsSubscriptions$.next(false)),
      tap(data => this.cardsListSubscriptions$.next(data))
    );
  }

  public saveNewCard(card: ICard) {
    return this.apiService
      .post('/payment-methods', {
        number: card.number,
        holder: card.name.toUpperCase(),
        month: card.expiry.substr(0, 2),
        year: card.expiry.substr(3, 4),
        cvv: card.cvc
      })
      .pipe(
        map(response => response.data),
        tap(data => {
          const updatedListCards = [data, ...this.cardsList$.value];

          this.cardsList$.next(updatedListCards);
        })
      );
  }

  public loadCardUsage(cardId: string): Observable<ISubscriptions[]> {
    this.loadingCardUsage$.next(true);
    return this.apiService.get(`/payment-methods/usage/${cardId}`).pipe(
      map(response => response.data),
      tap(data => this.cardUsage$.next(data)),
      tap(() => this.loadingCardUsage$.next(false))
    );
  }

  public getCardUsage(): Rx.BehaviorSubject<ISubscriptions[]> {
    return this.cardUsage$;
  }

  public loadingCardUsage(): Rx.BehaviorSubject<boolean> {
    return this.loadingCardUsage$;
  }

  public cleanCardUsage(): Observable<boolean> {
    return Rx.of(true).pipe(
      tap(() => {
        this.loadingCardUsage$.next(true);
        this.cardUsage$.next([]);
      })
    );
  }

  public deleteCard(cardId: number) {
    return this.apiService.delete(`/payment-methods/${cardId}`).pipe(
      tap(() => {
        const updatedListCards = this.cardsList$.value.filter(card => card.id !== cardId);

        this.cardsList$.next(updatedListCards);
      })
    );
  }

  public checkIfUserHasCoproductionGains(): Observable<boolean> {
    return this.apiService.get('/customers/has_coproduction_gains').pipe(map(response => response.data));
  }

  public checkIfUserActivated24hProfit() {
    return this.apiService.get('/customers/check/twentyfourhoursprofit').pipe(map(response => response.data));
  }
}

const CustomerFactory = new CustomerService(apiService);
export const customerFactory = CustomerFactory;
